import React, { useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { Branch, Restaurant } from '../../../services/api';

interface BranchesViewProps {
  restaurant: Restaurant;
  branches: Branch[];
  onBack: () => void;
  onCreateBranch: () => void;
  onToggleBranch: (branchId: string, isActive: boolean) => void;
}

export const BranchesView = React.memo(function BranchesView({
  restaurant,
  branches,
  onBack,
  onCreateBranch,
  onToggleBranch,
}: BranchesViewProps): React.JSX.Element {
  const safeBranches = useMemo(() => Array.isArray(branches) ? branches.filter(b => b?._id) : [], [branches]);
  const restaurantName = useMemo(() => restaurant?.name || 'Unknown', [restaurant?.name]);

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={onBack}
            className="flex items-center justify-center w-10 h-10 rounded-lg bg-zinc-700 dark:bg-zinc-700 bg-zinc-200 hover:bg-zinc-600 dark:hover:bg-zinc-600 hover:bg-zinc-300 text-white dark:text-white text-zinc-900 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-3xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">
              Branches - {restaurantName}
            </h2>
            <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600">
              Manage restaurant branches
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <button
          onClick={onCreateBranch}
          className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-500 text-white px-4 py-2 rounded-xl hover:from-amber-500 hover:to-amber-600 transition-all font-medium"
        >
          <Plus className="w-5 h-5" />
          New Branch
        </button>
      </div>

      {safeBranches.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="w-24 h-24 bg-gradient-to-br from-amber-600/20 to-amber-500/20 dark:from-amber-600/20 dark:to-amber-500/20 from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center mb-6">
            <Plus className="w-12 h-12 text-amber-500" />
          </div>
          <h3 className="text-xl font-serif text-amber-50 dark:text-amber-50 text-zinc-900 mb-2">No branches found</h3>
          <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm mb-6 text-center max-w-md">
            Get started by creating the first branch for this restaurant.
          </p>
          <button
            onClick={onCreateBranch}
            className="flex items-center gap-2 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-white px-6 py-3 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl font-medium"
          >
            <Plus className="w-5 h-5" />
            Create Branch
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {safeBranches.map((branch) => {
            if (!branch?._id) return null;
            return (
            <div key={branch._id} className="bg-gradient-to-br from-zinc-800 to-zinc-800/50 dark:from-zinc-800 dark:to-zinc-800/50 from-white to-zinc-50 rounded-2xl border border-amber-600/20 dark:border-amber-600/20 border-amber-300/20 p-4 shadow-xl">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-amber-50 dark:text-amber-50 text-zinc-900 font-semibold mb-2">{branch.name || 'Unnamed Branch'}</h3>
                  {branch.restaurantId && typeof branch.restaurantId === 'object' && branch.restaurantId.name && (
                    <p className="text-zinc-500 dark:text-zinc-500 text-zinc-600 text-xs mb-2">Restaurant: {branch.restaurantId.name}</p>
                  )}
                  {branch.address && <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm mb-1">{branch.address}</p>}
                  {branch.phone && <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm mb-1">{branch.phone}</p>}
                  {branch.email && <p className="text-zinc-400 dark:text-zinc-400 text-zinc-600 text-sm mb-2">{branch.email}</p>}
                </div>
                <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                  branch.isActive ? 'bg-green-500/20 dark:bg-green-500/20 bg-green-100 text-green-400 dark:text-green-400 text-green-700' : 'bg-red-500/20 dark:bg-red-500/20 bg-red-100 text-red-400 dark:text-red-400 text-red-700'
                }`}>
                  {branch.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <button
                onClick={() => onToggleBranch(branch._id, !branch.isActive)}
                className={`w-full px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                  branch.isActive
                    ? 'bg-red-600/20 dark:bg-red-600/20 bg-red-100 hover:bg-red-600/30 dark:hover:bg-red-600/30 hover:bg-red-200 text-red-400 dark:text-red-400 text-red-700'
                    : 'bg-green-600/20 dark:bg-green-600/20 bg-green-100 hover:bg-green-600/30 dark:hover:bg-green-600/30 hover:bg-green-200 text-green-400 dark:text-green-400 text-green-700'
                }`}
              >
                {branch.isActive ? 'Deactivate' : 'Activate'}
              </button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

